import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

export const DetailsSection = ({
  open,
  onToggle,
  children,
  placeholder,
}: {
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  /** Shown open in place of the details panel, for example a loading shimmer. */
  placeholder?: ReactNode;
}) => {
  const intl = useIntl();
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="-mt-2 flex flex-col gap-2 ps-12">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onToggle}
        aria-expanded={open}
        data-testid="routine-proposal-details-toggle"
        className="!h-auto w-fit gap-1.5 !bg-transparent !p-0 font-normal text-text-secondary hover:!bg-transparent hover:text-text-primary active:!bg-transparent aria-expanded:!bg-transparent"
      >
        {intl.formatMessage({
          defaultMessage: 'Details',
          id: 'O8trvGnX8u',
          description: 'Toggle to expand the read-only details on a routine proposal',
        })}
        <Chevron className={cn('size-4', !open && 'rtl:-scale-x-100')} aria-hidden />
      </Button>
      {open ? (placeholder ?? <DetailsPanel>{children}</DetailsPanel>) : null}
    </div>
  );
};

// Two lines of context above the first change when the panel opens on it.
const FIRST_CHANGE_INSET_PX = 40;

const DetailsPanel = ({ children }: { children: ReactNode }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);

  const updateFade = () => {
    const el = scrollRef.current;
    if (!el) {
      setShowFade(false);
      setShowTopFade(false);
      return;
    }
    const overflowing = el.scrollHeight - el.clientHeight > 1;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    setShowFade(overflowing && !atBottom);
    setShowTopFade(overflowing && el.scrollTop > 1);
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const firstChange = el?.querySelector<HTMLElement>('[data-change]');
    if (!el || !firstChange) return;
    // Relative to the current scrollTop, so a second run (Strict Mode) is a no-op.
    el.scrollTop = Math.max(
      0,
      el.scrollTop +
        firstChange.getBoundingClientRect().top -
        el.getBoundingClientRect().top -
        FIRST_CHANGE_INSET_PX
    );
  }, []);

  useLayoutEffect(() => {
    updateFade();
  });

  return (
    <div className="relative overflow-hidden rounded-lg bg-background-tertiary">
      <div
        ref={scrollRef}
        className="scrollbar-subtle max-h-62 overflow-y-auto px-3.5 py-3"
        data-testid="routine-proposal-details-panel"
        onScroll={updateFade}
      >
        {children}
      </div>
      {showTopFade ? (
        <div
          data-testid="routine-proposal-details-fade-top"
          className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-background-tertiary to-transparent"
        />
      ) : null}
      {showFade ? (
        <div
          data-testid="routine-proposal-details-fade"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-background-tertiary to-transparent"
        />
      ) : null}
    </div>
  );
};
